import React from 'react';
import { Modal, ModalProps } from 'antd';
import { Triangle, TriangleAlert, X } from 'lucide-react';
import { ReactNode } from 'react';

interface LmsModalProps extends ModalProps {
    open: boolean;
    title?: string;
    onOk?: () => void;
    onCancel?: () => void;
    okText?: string;
    cancelText?: string;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    header?: React.ReactNode; // Custom header JSX
    modalProps?: ModalProps;
    destroyOnClose?: boolean;
    headerIcon?: ReactNode;
    subtitle?: string | ReactNode;
    headerIconClassName?: string;
    mode?: string;
    contentClassName?: string; // Custom className for content area
    className?: string;
    centered?: boolean;
}

const LmsModal: React.FC<LmsModalProps> = ({
    open,
    title = '',
    onOk,
    onCancel,
    okText = 'OK',
    cancelText = 'Hủy',
    headerIcon,
    subtitle,
    children,
    footer,
    header,
    modalProps = {},
    destroyOnClose = false,
    headerIconClassName = '',
    mode,
    contentClassName = '',
    className,
    centered,
    ...rest
}) => {
    return (
        <Modal
            open={open}
            title={null}
            footer={null}
            closable={false}
            maskClosable={false}
            destroyOnClose={destroyOnClose}
            {...modalProps}
            {...rest}
            className={className}
            centered={centered}
            styles={{
                content: {
                    padding: 0,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                },
            }}
        >
            <div className='flex flex-col flex-1 min-h-0 bg-white rounded-3xl'>
                {/* Header */}
                {header ? (
                    <div className='w-full shrink-0'>
                        {header}
                    </div>
                ) : (
                    <div className='w-full flex items-center justify-between px-4 py-2 border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-t-3xl shrink-0 border-b border-gray-200'>
                        <div className='flex items-center gap-4'>
                            {headerIcon && (
                                <div
                                    className={`flex items-center justify-center w-10 h-10 rounded-xl text-white  ${headerIconClassName || 'bg-sky-500'
                                        }`}
                                >
                                    {headerIcon}
                                </div>
                            )}
                            {mode === 'delete' && (
                                <div
                                    className='flex items-center justify-center w-12 h-12 rounded-full'
                                    style={{ backgroundColor: '#FEE2E2' }}
                                >
                                    <TriangleAlert size={26} style={{ color: 'DC2626' }} />
                                </div>
                            )}

                            <div>
                                <h1 className=' font-medium text-gray-900 2xl:text-base'>
                                    {title}
                                </h1>
                                {subtitle && <p className='text-sm text-gray-500'>{subtitle}</p>}
                            </div>
                        </div>

                        <button
                            onClick={onCancel}
                            className='group flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 cursor-pointer'
                            aria-label='Close modal'
                        >
                            <X className='w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors' />
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className={`flex-1 min-h-0 overflow-y-auto border p-4 ${contentClassName}`}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className='flex justify-end gap-2 shrink-0 p-4 border-t bg-white'>
                        {footer}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default LmsModal;
